import React, { useEffect, useState } from "react";
import { api, formatError, resolveImageUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock, Check, X, Sparkle } from "@phosphor-icons/react";

export default function BannerPicker({ open, onOpenChange, currentBannerId, onChange }) {
  const [banners, setBanners] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/banners");
        setBanners(data);
      } catch (e) { /* silent */ }
    })();
  }, [open]);

  async function pick(b) {
    if (!b.unlocked) return;
    setBusy(true);
    try {
      const { data } = await api.put("/me/banner", { banner_id: b.banner_id });
      onChange?.(data);
      toast.success("Banner atualizado");
      onOpenChange(false);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  async function clearBanner() {
    setBusy(true);
    try {
      const { data } = await api.put("/me/banner", { banner_id: "" });
      onChange?.(data);
      toast.success("Banner removido");
      onOpenChange(false);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0e0e13] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="banner-picker">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Sparkle size={22} weight="fill" className="text-[#E63946]" /> Escolhe o teu Banner
          </DialogTitle>
        </DialogHeader>

        {banners.length === 0 && (
          <div className="py-12 text-center text-zinc-500 font-mono-accent text-xs">
            SEM BANNERS DISPONÍVEIS AINDA
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          {banners.map((b) => {
            const isCurrent = b.banner_id === currentBannerId;
            return (
              <div key={b.banner_id} className="space-y-2" data-testid={`banner-option-${b.banner_id}`}>
                <button
                  type="button"
                  disabled={!b.unlocked || busy}
                  onClick={() => pick(b)}
                  className={`relative w-full aspect-[16/6] rounded-xl overflow-hidden border-2 transition-all group ${
                    isCurrent
                      ? "border-[#E63946] ring-2 ring-[#E63946]/40"
                      : b.unlocked
                      ? "border-white/10 hover:border-[#E63946] cursor-pointer"
                      : "border-white/5 cursor-not-allowed"
                  }`}
                >
                  <img
                    src={resolveImageUrl(b.image_url)}
                    alt={b.name}
                    className={`w-full h-full object-cover transition-all ${!b.unlocked ? "grayscale brightness-50" : "group-hover:scale-105"}`}
                  />
                  {!b.unlocked && (
                    <div className="absolute inset-0 grid place-items-center bg-black/30">
                      <Lock size={32} weight="fill" className="text-white/80" />
                    </div>
                  )}
                  {isCurrent && (
                    <div className="absolute top-2 right-2 bg-[#E63946] text-white text-xs font-display font-bold px-2 py-1 rounded-full flex items-center gap-1">
                      <Check size={12} weight="bold" /> ATUAL
                    </div>
                  )}
                  {b.is_exclusive && (
                    <div className="absolute top-2 left-2 bg-gradient-to-r from-[#F59E0B] to-[#E63946] text-white text-[10px] font-mono-accent font-bold px-2 py-1 rounded-full">
                      EXCLUSIVO
                    </div>
                  )}
                </button>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold text-sm truncate">{b.name}</div>
                    {b.lock_reason ? (
                      <div className="text-[11px] text-[#F59E0B] mt-0.5 flex items-center gap-1">
                        <Lock size={10} weight="bold" /> {b.lock_reason}
                      </div>
                    ) : (
                      <div className="text-[11px] text-emerald-400 mt-0.5 flex items-center gap-1">
                        <Check size={10} weight="bold" /> Disponível
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {currentBannerId && (
          <div className="mt-6 pt-4 border-t border-white/10 flex justify-end">
            <Button variant="outline" onClick={clearBanner} disabled={busy} className="border-white/15 text-zinc-300 hover:bg-white/5" data-testid="banner-clear-btn">
              <X size={14} weight="bold" className="mr-1.5" /> Remover banner
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
